'use strict'
/** @import ./../../common/types */

import * as React from 'react'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxRountedIcon from '@mui/icons-material/CheckBoxRounded'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { pJson } from '../../common/utils/StringUtils'

import { AutoSizer, List } from 'react-virtualized'
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux'
import { produce } from 'immer'

/**
 * @function FilterGroup
 * @param {Group} props
 */
export const FilterGroup = (props) => {
  const { title, className = '', controlIdList, hasBorder = true } = props

  return (
    <div className={` ${hasBorder && 'border-b'} border-solid  py-5 text-base ${className} `}>
      <p className="mb-3">{title}</p>

      {controlIdList.map((id, index) => {
        return <FilterControl key={`control_${id}_${index}`} id={id} />
      })}
    </div>
  )
}

/**
 * @function FilterControl
 * @param {Control} props
 */
const FilterControl = (props) => {
  const { id } = props

  const control = useSelector((s) => s.controlState.get(id))

  const dispatch = useDispatch()

  const {
    label = '',
    className = '',
    labelClassName = '',
    optionIdList = [],
    type = '',
    state = { show: true, disabled: false }
  } = control || {}

  const _onUIChange = (newState) => {
    dispatch({ type: 'SET_CONTROL', payload: newState })
  }

  const filterControlProps = {
    control,
    optionIdList,
    state: control?.state || { show: true, disabled: false },
    onUIChange: _onUIChange
  }

  const renderOptions = (type) => {
    switch (type) {
      case 'text':
        return <FilterText {...filterControlProps} />
      case 'select':
        return <FilterSelect {...filterControlProps} />
      case 'multiselect':
        return <FilterMultiSelect {...filterControlProps} />
      case 'range':
        return <FilterRange {...filterControlProps} />
      case 'checkbox':
        return <FilterCheckbox {...filterControlProps} />
      case 'radio':
        return <FilterRadio {...filterControlProps} />
      default:
        return <React.Fragment />
    }
  }

  if (!state.show) return <></>

  return (
    <div className={`text-sm ${className} mb-3`} title={window.debug_ && pJson(control)} data-testid={id}>
      <p className={`${labelClassName}`}>{label}</p>
      {renderOptions(type)}
    </div>
  )
}

/**
 * @param {string[]}
 * @returns {Map<string, Option>}
 */
const useOptions = (optionIdList) => {
  const entries = useSelector((e) => optionIdList.map((option) => [option, e.optionState.get(option)]), shallowEqual)
  return React.useMemo(() => new Map(entries), [entries])
}

/**
 * @function FilterTextコンポネント
 * @param {FilterOption} props
 */
const FilterText = (props) => {
  const { optionIdList } = props

  const options = useOptions(optionIdList)
  const dispatch = useDispatch()

  const handleFilterText = async (e, id) => {
    const newVal = e.target.value
    if (newVal === undefined) return
    const value = newVal.trim()

    dispatch({ type: 'SET_OPTION_VALUE', payload: { id, value } })
  }

  return (
    <>
      {optionIdList.map((id) => {
        const option = options.get(id)

        const { label = '', labelClassName = '', valueClassName = '', placeholder, inputProps } = option
        const key = `control_${option.id}`

        return (
          <div key={key} className={`${''}`} data-testid="control">
            <label className={`${labelClassName}`} htmlFor={`option_${option.id}`}>
              {label}
            </label>
            <input
              type="text"
              className={`text-left w-full ${valueClassName} p-2 pl-4 border border-solid border-gray-400/50 rounded-xl mb-1  flex justify-between h-[46px] items-center`}
              id={key}
              placeholder={placeholder}
              {...inputProps}
              value={option.state.value}
              onChange={(e) => {
                handleFilterText(e, id)
                options?.onChange?.(e, id)
              }}
            />
          </div>
        )
      })}
    </>
  )
}

/**
 * @function FilterSelectコンポネント
 * @param {FilterOption} props
 */
const FilterSelect = React.forwardRef((props, ref) => {
  const { optionIdList, state, control } = props

  const [filterText, setFilterText] = React.useState('')

  const { showSearch = true, showDropdown = false } = state
  const options = useOptions(optionIdList)
  const dispatch = useDispatch()

  // 検索をしてフィルター
  const filteredOptions = React.useMemo(() => {
    if (!filterText?.trim()) return optionIdList
    const filteredList = optionIdList.filter((id) => options.get(id).label.includes(filterText))
    return filteredList.length > 0 ? filteredList : []
  }, [filterText, optionIdList, options])

  // プルダウンを切り替え
  const toggleDropdown = () => {
    dispatch({
      type: 'SET_CONTROL_STATE',
      payload: { id: control.id, path: 'state.showDropdown', value: !showDropdown }
    })
  }

  // ラベルの表示
  const mainLabelText = () => {
    const notSelected = '未選択'
    const selectedOptionKey = optionIdList.find((e) => options.get(e).state.value)

    const option = options.get(selectedOptionKey)

    if (!option) return notSelected

    //ラベルに表示しない設定
    if (option?.state?.showLabel !== undefined && !option?.state?.showLabel) return notSelected

    return option?.displayLabel ?? option?.label ?? notSelected
  }

  // フィルターの処理
  const handleFilter = (e) => {
    const filterValue = e.target.value.trim()
    if (filterValue === undefined) return

    setFilterText(filterValue)
  }

  const handleSelectOption = (id) => {
    optionIdList.forEach((e) => {
      dispatch({ type: 'SET_OPTION_VALUE', payload: { id: e, value: e === id } })
    })

    options.get(id).onChange?.(id)
  }

  return (
    <>
      <div className="relative text-base  " data-testid="control">
        <div
          className={`p-2 pl-4 border border-solid border-gray-400/50 rounded-xl mb-1  flex justify-between h-[46px] items-center ${
            showDropdown && 'border-blue-400'
          }`}
          role="button"
          onClick={toggleDropdown}
        >
          <span className="truncate text-[14px]">{mainLabelText()} </span>{' '}
          <>
            {' '}
            <ExpandLess
              data-testid="toggleArrow"
              sx={{ transition: 'all 300ms ease-in-out', transform: !showDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </>
        </div>

        <div
          className={`p-4 ${
            showDropdown ? 'block' : 'hidden'
          } border border-solid border-gray-400/50  w-full  z-[2] bg-white rounded-xl shadow-md `}
        >
          <div className={` relative ${showSearch ? 'block' : 'hidden'}`}>
            <input
              data-testid="searchOptions"
              type="text"
              name=""
              value={filterText}
              className="px-4 py-1 w-full pr-[10px] h-[46px] text-[14px] rounded-xl  "
              placeholder="値を検索"
              onChange={handleFilter}
            />

            <span className=" icon-search absolute right-[10px] top-[50%] -translate-y-[50%] text-[32px]  "></span>
          </div>

          <ul className={` ${showSearch ? 'mt-4' : ''} min-h-[350px] `} data-testid="optionList">
            <AutoSizer>
              {({ width, height }) => {
                return (
                  <List
                    width={width}
                    height={height - 20}
                    rowCount={filteredOptions.length}
                    rowHeight={30}
                    rowRenderer={({ index, key, style }) => {
                      const id = filteredOptions[index]
                      return (
                        <SingleSelectOption key={key} id={id} style={style} handleSelectOption={handleSelectOption} />
                      )
                    }}
                  />
                )
              }}
            </AutoSizer>
          </ul>
        </div>
      </div>
    </>
  )
})

/**
 * Selectの選択
 *
 * @param {{ id: string; style: Record<string, string> }} props
 */
const SingleSelectOption = (props) => {
  const { id, style, handleSelectOption } = props

  const option = useSelector((s) => s.optionState.get(id))

  if (!option) return null

  const show = option.state.show === undefined ? true : option.state.show

  const key = `single_select_${id}`

  const handleToggle = () => {
    handleSelectOption(id)
  }

  const selected = option.state.value

  return (
    <li style={style} className={`flex gap-x-2 mt-1 ${!show && 'bg-red-400'}  items-center`} key={key}>
      <Radio
        sx={{ padding: 0 }}
        size="small"
        id={key}
        checked={selected}
        {...option.inputProps}
        onChange={handleToggle}
      />

      <label
        htmlFor={key}
        className={`shrink-0 text-[14px] max-w-[90%] truncate ${option?.labelClassName} `}
        title={option?.label + (window.debug_ ? pJson(option).toString() : '')}
      >
        {option?.label}
        {'  '}
        {option?.extra && (
          <span title={option?.extra} className="truncate  text-gray-400  text-[14px]">
            ( {option?.extra} )
          </span>
        )}
      </label>
    </li>
  )
}

/**
 * @function FilterMultiSelectコンポネント
 * @param {FilterOption} props
 */
const FilterMultiSelect = React.forwardRef((props, ref) => {
  const { control, optionIdList, state } = props

  const [filterText, setFilterText] = React.useState('')

  const { showSearch = true, showAllOption = true, showDropdown = false } = state || {}

  const options = useOptions(optionIdList)
  const store = useStore()
  const dispatch = useDispatch()

  const unselectedText = control?.state?.unselectedText || ''

  // 検索をしてフィルター
  const filteredOptions = React.useMemo(() => {
    const optionList = optionIdList.filter((id) => {
      const option = options.get(id)
      const optionShowState = option?.state?.show
      return optionShowState === undefined ? true : optionShowState
    })

    if (!filterText?.trim()) return optionList

    const filteredList = optionList.filter((id) => {
      const option = options.get(id)
      return option.label.includes(filterText)
    })

    return filteredList.length > 0 ? filteredList : []
  }, [filterText, optionIdList, options])

  const isAllSelected = filteredOptions.every((id) => options.get(id).state.value)

  // プルダウンを切り替え
  const toggleDropdown = () => {
    dispatch({
      type: 'SET_CONTROL_STATE',
      payload: { id: control.id, path: 'state.showDropdown', value: !showDropdown }
    })
  }

  const combinedText = React.useMemo(() => {
    return optionIdList
      .map((id) => {
        const option = options.get(id)
        if (option.state.value) return option?.label
      })
      .filter(Boolean)
      .join()
  }, [options])

  // ラベルの表示
  const mainLabelText = () => {
    const all = 'すべて'

    if (isAllSelected) {
      return all
    }

    return combinedText || unselectedText
  }

  // フィルターの処理
  const handleFilter = (e) => {
    const filterValue = e.target.value.trim()
    if (filterValue === undefined) return
    setFilterText(filterValue)
  }

  // 選択すべてを切り替え
  const handleToggleAll = () => {
    const optionState = store.getState().optionState

    if (isAllSelected) {
      const newState = produce(optionState, (draft) => {
        filteredOptions.forEach((e) => {
          draft.get(e).state.value = false
        })
      })

      dispatch({ type: 'SET_OPTION', payload: newState })

      filteredOptions.forEach((e) => {
        options.get(e).onChange(false)
      })
    } else {
      const newState = produce(optionState, (draft) => {
        filteredOptions.forEach((e) => {
          draft.get(e).state.value = true
        })
      })

      dispatch({ type: 'SET_OPTION', payload: newState })

      filteredOptions.forEach((e) => {
        options.get(e).onChange(true)
      })
    }
  }

  React.useEffect(() => {
    const resetButton = document.getElementById('filterResetButton')

    if (!resetButton) return

    const abortController = new AbortController()
    resetButton.addEventListener('click', () => setFilterText(''), { signal: abortController.signal })
    return () => {
      abortController.abort()
    }
  }, [])

  return (
    <>
      <div className="relative text-base  " data-testid="control">
        <div
          className={`p-2 pl-4 border border-solid border-gray-400/50 rounded-xl mb-1  flex justify-between h-[46px] items-center ${
            showDropdown && 'border-blue-400'
          }`}
          role="button"
          onClick={toggleDropdown}
        >
          <span className="truncate text-[14px]">{mainLabelText()} </span>{' '}
          <>
            {' '}
            <ExpandLess
              data-testid="toggleArrow"
              sx={{ transition: 'all 300ms ease-in-out', transform: !showDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </>
        </div>

        <div
          className={`p-4 ${
            showDropdown ? 'block' : 'hidden'
          } border border-solid border-gray-400/50  w-full  z-[2] bg-white rounded-xl shadow-md `}
        >
          <div className={`relative ${showSearch ? 'block' : 'hidden'}`}>
            <input
              data-testid="searchOptions"
              type="text"
              name=""
              value={filterText}
              className="px-4 py-1 w-full pr-[10px] h-[46px] text-[14px] rounded-xl pr-[50px]  "
              placeholder="値を検索"
              onChange={handleFilter}
            />

            <span className=" icon-search absolute right-[10px] top-[50%] -translate-y-[50%] text-[32px]  "></span>
          </div>

          <ul className={' mt-4 min-h-[350px] '} data-testid="optionList">
            <li className={`flex gap-x-2 mt-2 items-center  ${showAllOption ? 'block' : 'hidden'} `}>
              <Checkbox
                size="small"
                checkedIcon={<CheckBoxRountedIcon />}
                id={`select_all_${control.id}`}
                disabled={filteredOptions.length === 0}
                checked={isAllSelected}
                onChange={handleToggleAll}
              />

              <label htmlFor={`select_all_${control.id}`} className="text-[14px]">
                すべて
              </label>
              <span className=""></span>
            </li>

            <AutoSizer>
              {({ width, height }) => {
                return (
                  <List
                    height={height - 20}
                    rowCount={filteredOptions.length}
                    rowHeight={30}
                    rowRenderer={({ index, key, style }) => {
                      const id = filteredOptions[index]

                      return <MultiSelectOption style={style} id={id} key={key} />
                    }}
                    width={width}
                  />
                )
              }}
            </AutoSizer>
          </ul>
        </div>
      </div>
    </>
  )
})

const MultiSelectOption = (props) => {
  const { id, style } = props
  const option = useSelector((s) => s.optionState.get(id))
  const dispatch = useDispatch()

  if (!option) return null

  const show = option.state.show === undefined ? true : option.state.show

  const key = `multi_select_${id}`

  const handleToggleOption = () => {
    dispatch({ type: 'SET_OPTION_VALUE', payload: { id, value: !option.state.value } })
    dispatch({ type: 'SET_OPTION_STATE', payload: { path: 'state.show', value: !option.state.value } })
    option?.onChange?.(!option.state.value)
  }

  if (!show) return <React.Fragment />

  return (
    <li style={style} className={` gap-x-2 mt-1  ${show ? 'flex' : 'none'}  items-center`}>
      <Checkbox
        id={key}
        size="small"
        disabled={!show}
        checkedIcon={<CheckBoxRountedIcon />}
        checked={option.state.value}
        onChange={handleToggleOption}
      />

      <label
        htmlFor={key}
        className={`shrink-0 text-[14px] ${option?.labelClassName} truncate max-w-[90%]`}
        title={option?.label + (window.debug_ ? pJson(option).toString() : '')}
      >
        {option.label}
      </label>

      {option.extra && (
        <span title={option.extra} className="truncate  text-gray-400  text-[14px]">
          ( {option.extra} )
        </span>
      )}
    </li>
  )
}

/**
 * @function FilterCheckboxコンポネント
 * @param {Option} props
 */
const FilterCheckbox = (props) => {
  const { id, label, labelClassName, value, valueClassName } = props
  return <>FilterCheckbox の実装</>
}

/**
 * @function FilterRangeコンポネント
 * @param {Option} props
 */
const FilterRange = (props) => {
  const { id, label, labelClassName, value, valueClassName } = props
  return <>FilterRange の実装</>
}

/**
 * @function FilterRadioコンポネント
 * @param {FilterOption} props
 */
const FilterRadio = (props) => {
  const { optionIdList } = props

  const options = useOptions(optionIdList)
  const dispatch = useDispatch()

  const selected = optionIdList.find((e) => options.get(e).state.value)

  const handleSelectOption = (id) => {
    optionIdList.forEach((e) => {
      dispatch({ type: 'SET_OPTION_VALUE', payload: { id: e, value: e === id } })
    })

    options.get(id)?.onChange?.(options.get(id).value)
  }

  return (
    <RadioGroup
      sx={{ columnGap: 1, marginTop: 1 }}
      row
      value={selected}
      onChange={(e) => handleSelectOption(e.target.value)}
      data-testid="control"
    >
      {optionIdList.map((_id) => {
        const {
          id,
          label,
          labelClassName = '',
          valueClassName = '',
          state = { show: true, disabled: false }
        } = options.get(_id)

        const key = `option_${id}`

        if (!state.show) return <React.Fragment key={key} />

        return (
          <React.Fragment key={key}>
            <FormControlLabel
              sx={{ margin: 0 }}
              componentsProps={{ typography: { fontSize: '0.875rem' } }}
              label={label}
              value={id}
              control={<Radio sx={{ padding: 0, paddingRight: '4px' }} size="small" className={valueClassName} />}
              className={labelClassName}
            />
          </React.Fragment>
        )
      })}
    </RadioGroup>
  )
}
